import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Lock, User } from "@phosphor-icons/react";

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) navigate("/");
    else setErr(res.error || "Login failed");
  };

  return (
    <div className="min-h-screen bg-white grid lg:grid-cols-2">
      {/* Left: marketing */}
      <div className="hidden lg:flex flex-col justify-between bg-[#1e293b] text-white p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <img src="/logo-gate.jpg" alt="GATE" className="w-14 h-14 brut-border border-white object-cover" />
            <div>
              <div className="text-[11px] font-display tracking-[0.4em] text-[#60a5fa]">// OPERATIONS CONTROL</div>
              <div className="font-display text-2xl uppercase tracking-tight mt-0.5">US Operations</div>
            </div>
          </div>
          <div className="font-display text-6xl uppercase tracking-tighter mt-6 leading-[0.95]">
            Route<br />Card<br />Platform
          </div>
          <div className="font-display uppercase text-sm tracking-widest mt-6 text-slate-300 max-w-md">
            Inward GRN · Assembly · Box traceability · Packing lists
          </div>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-4 max-w-md">
          {[
            { k: "GRN", v: "Inward" },
            { k: "AR", v: "Assembly" },
            { k: "PL", v: "Shipment" },
          ].map((s) => (
            <div key={s.k} className="border-2 border-[#60a5fa] p-3">
              <div className="font-display text-2xl text-[#60a5fa]">{s.k}</div>
              <div className="text-xs uppercase tracking-widest text-slate-300">{s.v}</div>
            </div>
          ))}
        </div>

        <div className="relative z-10 text-[10px] font-mono-ui tracking-widest uppercase text-slate-500">
          Secure · Traceable · Auditable
        </div>

        {/* Decorative grid */}
        <div className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "linear-gradient(to right, #60a5fa 1px, transparent 1px), linear-gradient(to bottom, #60a5fa 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        <div className="absolute right-[-50px] bottom-[-50px] w-[280px] h-[280px] border-[16px] border-[#60a5fa]" />
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="brut-tag brut-tag-yellow mb-6">Secure Login</div>
          <h1 className="font-display text-5xl uppercase tracking-tighter">Sign In.</h1>
          <p className="text-sm text-zinc-600 mt-2 font-mono-ui">
            Operators, admins & viewers — punch your credentials.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="brut-label flex items-center gap-1.5"><User size={12} weight="bold" /> Email</label>
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@usops.com"
                className="brut-input mt-1.5"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="brut-label flex items-center gap-1.5"><Lock size={12} weight="bold" /> Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="brut-input mt-1.5"
                data-testid="login-password-input"
              />
            </div>

            {err && (
              <div className="brut-tag brut-tag-red w-full text-center" data-testid="login-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="brut-btn brut-btn-primary w-full px-4 py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {submitting ? "Signing in..." : "Sign In"}
              <ArrowRight size={16} weight="bold" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
